import React, { useState } from 'react';
import Drawer, { FooterSpacer } from '../../Drawer';
import Button from '../../Button';
import Field from '../../Field';
import { useNoteTemplates } from '../../../data/useNoteTemplates';
import Unread from '../../Unread';
import { READING_WORDING as RW } from '../../../domain/reading';
import { isCompleteTemplate, TEMPLATE_LABEL_MAX, TEMPLATE_BODY_MAX } from '../../../domain/noteTemplates';
import s from './noteTemplates.module.css';

// Options → Lesson Note Templates, the item MMS has in that menu.
//
// A list of what is saved over a form that adds to it, like CategoriesDrawer.
// Unlike it, a template is edited in place: its body is the paragraph the
// feature exists to avoid typing twice.

const emptyDraft = () => ({ id: null, label: '', body: '' });

export default function NoteTemplatesDrawer({ open, onClose }) {
  // `loaded`: the templates have answered (#629) — until then the list says
  // nothing rather than "No templates yet."
  const {
    templates, loaded, failed, saveTemplate, removeTemplate,
  } = useNoteTemplates();
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const submit = async () => {
    if (!isCompleteTemplate(draft)) return;
    setBusy(true);
    try {
      await saveTemplate(draft);
      setDraft(emptyDraft());
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await removeTemplate(id);
      // Deleting the template currently loaded into the form would otherwise
      // leave the form offering to save it back.
      setDraft((d) => (d.id === id ? emptyDraft() : d));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Lesson Note Templates"
      width={620}
      footer={(
        <>
          <FooterSpacer />
          <Button onClick={onClose}>Done</Button>
        </>
      )}
    >
      <div className={s.lead}>
        Saved note bodies. Writing a lesson note, <strong>Templates</strong> under
        the box adds one to whichever note you are on — student, parent or
        private — without replacing what you have already typed.
      </div>

      {templates.length === 0 && !loaded ? (
        <Unread failed={failed} />
      ) : templates.length === 0 ? (
        <div className={s.empty}>{RW['noteTemplates.empty']}</div>
      ) : templates.map((t) => (
        <div key={t.id} className={s.row}>
          <div className={s.rowText}>
            <div className={s.rowLabel}>{t.label}</div>
            <div className={s.rowBody}>{t.body}</div>
          </div>
          <div className={s.rowActions}>
            <button className={s.linkButton} onClick={() => setDraft({ ...t })}>Edit</button>
            <button className={s.linkButton} onClick={() => remove(t.id)}>Remove</button>
          </div>
        </div>
      ))}

      <div className={s.addBlock}>
        <div className={s.addTitle}>{draft.id ? 'Edit template' : 'Add a template'}</div>

        <Field label="Name">
          <input
            className={s.input}
            value={draft.label}
            maxLength={TEMPLATE_LABEL_MAX}
            placeholder="Weekly practice"
            onChange={(e) => set({ label: e.target.value })}
          />
        </Field>

        <Field label="Note">
          <textarea
            rows={6}
            className={s.textarea}
            value={draft.body}
            maxLength={TEMPLATE_BODY_MAX}
            placeholder={'Scales: C and G, hands together, slowly.\nPiece: bars 1–16, five times.'}
            onChange={(e) => set({ body: e.target.value })}
          />
        </Field>

        <div className={s.addActions}>
          <Button onClick={submit} loading={busy} disabled={!isCompleteTemplate(draft)}>
            {draft.id ? 'Save Template' : 'Add Template'}
          </Button>
          {draft.id ? (
            <Button variant="secondary" onClick={() => setDraft(emptyDraft())}>Cancel</Button>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
